"use client";

import { Button } from "@universe/ui/components/button";
import { Sparkles, X } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useCallback, useMemo, useState } from "react";

import {
  getOnboardingStepRoute,
  type OnboardingType,
  Route,
} from "@/enums/route";
import { useCustomerAccount } from "@/providers";
import {
  BUSINESS_STEP_ORDER,
  getResumeStep,
  PERSONAL_STEP_ORDER,
  STEP_LABEL_KEYS,
} from "@/utils/onboardingProgress";
import {
  ApplicationState,
  ApplicationType,
  useGetCustomerAccountApplicationsQuery,
} from "@generated/redux/services/generated/api";

const SESSION_STORAGE_KEY = "resume-onboarding-banner-dismissed";

/**
 * Top-of-dashboard banner that nudges users with a draft onboarding
 * application back into the flow. Returns null when:
 *   - the applications query is loading or errored,
 *   - there is no draft application,
 *   - the application is already SUBMITTED / ADMITTED / ACCEPTED / OPENED,
 *   - the user has dismissed the banner this session.
 *
 * Dismissal is stored in sessionStorage so the banner reappears next session.
 */
export default function ResumeOnboardingBanner() {
  const t = useTranslations("onboarding");
  const router = useRouter();
  const { customerAccounts } = useCustomerAccount();

  const { data, isLoading, isError } = useGetCustomerAccountApplicationsQuery(
    {},
    { skip: (customerAccounts?.length ?? 0) > 0 }
  );

  // Read sessionStorage in a lazy state initializer — runs once on mount,
  // client-only ("use client"). Falls back to "not dismissed" on SSR or when
  // sessionStorage is unavailable (privacy mode).
  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(SESSION_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    try {
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
    } catch {
      // ignore — best-effort
    }
  }, []);

  // Find the first draft application (if any) and compute resume metadata.
  const draft = useMemo(() => {
    const apps = data?.data ?? [];
    return apps.find((app) => app.applicationState === ApplicationState.Draft);
  }, [data]);

  const resumeMeta = useMemo(() => {
    if (!draft) return null;

    const flowType: OnboardingType =
      draft.applicationType === ApplicationType.Corporate
        ? "business"
        : "personal";

    const stepOrder =
      flowType === "business" ? BUSINESS_STEP_ORDER : PERSONAL_STEP_ORDER;

    const resumeStep = getResumeStep(draft.formData, flowType);
    const resumeIndex = stepOrder.indexOf(resumeStep);

    // Route — prefer step route. If the application has no formData yet, the
    // user likely hasn't completed KYC/verification — fall back to the
    // verification entry point.
    const route =
      draft.formData == null
        ? Route.OnboardingVerification
        : getOnboardingStepRoute(flowType, resumeStep);

    return {
      route,
      stepNumber: resumeIndex >= 0 ? resumeIndex + 1 : 1,
      totalSteps: stepOrder.length,
      labelKey: STEP_LABEL_KEYS[resumeStep] ?? "",
    };
  }, [draft]);

  // Bail out early in any of the "render nothing" cases.
  if (isLoading || isError) return null;
  if (!draft || !resumeMeta) return null;
  if (isDismissed) return null;

  const stepLabel = resumeMeta.labelKey
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (t(resumeMeta.labelKey as any) as string)
    : "";

  const handleResume = () => {
    router.push(resumeMeta.route);
  };

  return (
    <div className="px-2 md:px-4">
      <div className="relative flex items-start gap-3 rounded-xl border border-brand-primary/30 bg-brand-primary/5 p-3 md:p-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-primary/10">
          <Sparkles className="h-5 w-5 text-brand-boundary" />
        </div>

        <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold text-gray-900 md:text-base">
              {t("resumeBanner.heading")}
            </span>
            <span className="text-xs text-muted-foreground md:text-sm">
              {t("resumeBanner.subtext", {
                current: resumeMeta.stepNumber,
                total: resumeMeta.totalSteps,
                step: stepLabel,
              })}
            </span>
          </div>

          <Button
            type="button"
            onClick={handleResume}
            className="self-start md:self-auto"
            data-testid="resume-onboarding-cta"
          >
            <span>{t("resumeBanner.cta")}</span>
          </Button>
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t("resumeBanner.dismiss")}
          className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-brand-primary/10 hover:text-gray-800"
          data-testid="resume-onboarding-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
